import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import { canAccessQuality } from '@/domain/workspace-access';
import { doc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import type { ConsentStatus, RequestTraceStatus } from "@moonshot/contracts";
import type { DashboardStatistics, DetailTab, RequestTraceView, WorkspaceEntity, WorkspaceMode } from "@/domain/types";
import {
  acquisitionChannel,
  activityLocation,
  classifyActivity,
  dateRangeForPreset,
  defaultLeadFitWeights,
  deriveEntityInsights,
  mapActivityIdentityKey,
  normalizedDeviceClass,
  setActiveLeadFitWeights,
  type ActivityClass,
  type DatePreset,
  type DeviceClassFilter,
  type LeadFitWeights
} from "@/domain/activity-insights";
import {
  aggregateActivityResults,
  aggregateActivityResultsByRecency,
  aggregateCompanySearchResults,
  deduplicateExpandedActivities,
  expandActivityOccurrences
} from "@/domain/activity-groups";
import { synchronizeAccountTraceActivities } from "@/domain/trace-activities";
import { demoEntities, demoStatistics, demoTimeSeries, demoTraces } from "@/data/demo";
import { apiFetch } from "@/services/api";
import { firebaseAuth, firebaseConfigured, firebaseDb } from "@/services/firebase";

const LIST_COLLAPSED_KEY = "moonshot:v2:list-collapsed";
const TARGET_ACCOUNTS_KEY = "moonshot:v2:target-accounts";
const SAVED_FILTERS_KEY = "moonshot:v2:saved-visitor-filters";
const ACTIVE_FILTERS_KEY = "moonshot:v2:active-visitor-filters";
const ACTIVITY_SUMMARY_KEY = "moonshot:v2:activity-summary";
export type EntityChoiceFilter = "all" | "yes" | "no";
export type VisitFrequencyFilter = "all" | "new" | "returning";
/** A manual company correction (see functions/src/company-corrections.ts). */
export type CompanyCorrectionRequest = {
  source: { kind: "account"; organizationId: string; visitId?: string }
    | { kind: "visitor"; visitorProfileId: string; sessionId?: string }
    | { kind: "trace"; requestTraceIds: string[] };
  scope: "visit" | "all";
  target: { organizationId: string } | { name: string; domain?: string };
  remember: boolean;
};
export type CompanyCorrectionResult = {
  correctionId: string;
  organizationId: string;
  entityId: string;
  moved: { signals: number; created: number; sessions: number; visits: number };
  learned: { requested: boolean; addresses: number; ranges: number; alias: boolean; visitor: boolean };
};
export interface VisitorFilterSnapshot {
  mode: WorkspaceMode;
  search: string;
  liveOnly: boolean;
  consent: ConsentStatus | "all";
  confidence: "all" | "published" | "candidate" | "unknown" | "suppressed";
  intent: string;
  acquisition?: string;
  confidenceMin?: number;
  country: string;
  countries?: string[];
  activityClasses?: ActivityClass[];
  devices?: DeviceClassFilter[];
  datePreset?: DatePreset;
  dateFrom: string;
  dateTo: string;
  frequency: VisitFrequencyFilter;
  reveal: EntityChoiceFilter;
  target: EntityChoiceFilter;
  crm: EntityChoiceFilter;
  sort: "recent" | "confidence" | "sessions" | "intent" | "lead_fit";
}
export interface SavedVisitorFilter {
  id: string;
  name: string;
  filters: VisitorFilterSnapshot;
}
export type TraceQueryFilters = {
  from?: string;
  to?: string;
  graphReleaseId?: string;
  modelReleaseId?: string;
  connectorVersion?: string;
  candidate?: string;
  resolver?: string;
};
type EntityPageResponse = {
  items: WorkspaceEntity[];
  nextCursor: string | null;
  hasMore: boolean;
};
type SettingsFieldsResponse = {
  fields: Array<{ id?: string; label: string; value: string | boolean }>;
};
export type CompanyEnrichmentResult = {
  organizationId: string;
  matched: boolean;
  updatedFields: string[];
  sources: NonNullable<WorkspaceEntity['companyDataSources']>;
  sourceResults?: NonNullable<WorkspaceEntity['companyDataSourceResults']>;
  checkedAt: string;
};
type LiveRevisionKind = "account" | "trace" | "consent" | "session" | "heartbeat" | "event" | "reveal" | "replay" | "admin";

function mergeLiveKinds(current?: LiveRevisionKind, incoming?: LiveRevisionKind): LiveRevisionKind | undefined {
  if (!current) return incoming;
  if (!incoming || current === incoming) return current;
  if (current === "heartbeat") return incoming;
  if (incoming === "heartbeat") return current;
  return "admin";
}
export type ConnectorHealth = {
  state: "missing_key" | "missing_graph" | "stale_connector" | "no_recent_traffic" | "limited_resolution" | "healthy";
  keyConfigured: boolean;
  graphReleaseId: string | null;
  graphExpiresAt: string | null;
  graphExpiresSoon: boolean;
  graphRemainingHours: number | null;
  lastTraceAt: string | null;
  lastSignalAt: string | null;
  lastGraphReleaseId: string | null;
  lastConnectorVersion: string | null;
  requiredConnectorVersion: string;
  connectorOutdated: boolean;
  graphMismatch: boolean;
  capabilitiesReported?: boolean;
  forwardConfirmedReverseDns?: "disabled" | "confirmation" | "discovery" | "unknown";
  companyEnrichment?: "disabled" | "prefix_consensus" | "exact" | "unknown";
  dynamicCompanyDiscoveryAvailable?: boolean;
  captureTransport?: "html_request" | "post_load_beacon" | "unknown";
  cdnSafeCaptureActive?: boolean;
  resolverCoverageLimited?: boolean;
};
export type SiteDirectoryItem = {
  id: string;
  name: string;
  environment?: string;
  primaryDomain?: string;
  setupStatus?: string;
  setupStep?: string;
  legalProfileStatus?: string;
  installationMode?: string;
  collectorMode?: string;
  managedValidationMode?: "managed_only" | "legacy_comparison";
  legacyComparisonOnly?: boolean;
  managedRelayPath?: string;
  publicTagId?: string;
  collectorVersion?: string;
  lastObservedAt?: string | null;
  lastVerifiedAt?: string | null;
  verificationSummary?: { browser?: string; consent?: string; edge?: string };
  managedCollectorHealth?: string;
  managedShadowGate?: import("@moonshot/contracts/managed-shadow-approval").ManagedShadowGateSummary | null;
};
export type OrganizationDirectoryItem = { id: string; name: string; roles: string[]; active: boolean };
const emptyStatistics: DashboardStatistics = {
  period: { days: 30, from: "", to: "", dataSource: "daily_rollups" },
  totalRequests: 0,
  identified: 0,
  recognizedCompanies: 0,
  candidates: 0,
  unknown: 0,
  suppressed: 0,
  resolverErrors: 0,
  consentedVisitors: 0,
  reveals: 0,
  precision: null,
  consentRate: 0,
  liveAccounts: 0,
  newCompanies: 0,
  returningCompanies: 0,
  newVisitors: 0,
  returningVisitors: 0,
  conversions: 0,
  conversionRate: 0,
  averageEngagementMs: 0,
  previous: { totalRequests: 0, identified: 0, reveals: 0, consentRate: 0 },
  changes: { totalRequests: 0, identified: 0, reveals: 0, consentRate: 0 },
  timeSeries: [],
  topCategories: [],
  topCompanies: [],
  topRegions: [],
  deviceBreakdown: [],
  acquisitionBreakdown: [],
  journeyFlows: [],
  consentBreakdown: [{ label: "Unbekannt", value: 1, count: 0, color: "#c8ced8" }],
  pageAnalytics: []
};

function isIdentifiedEntity(entity: WorkspaceEntity) {
  return classifyActivity(entity) === "recognized";
}

export function mergeWorkspaceEntitySummary(current: WorkspaceEntity, summary: WorkspaceEntity): WorkspaceEntity {
  if (!current.detailsLoaded || summary.detailsLoaded !== false) return summary;
  const precisionRank: Record<WorkspaceEntity["location"]["precision"], number> = {
    unknown: 0,
    country: 1,
    city: 2,
    network: 3,
    registered_office: 4,
    verified_site: 5
  };
  return {
    ...current,
    ...summary,
    detailsLoaded: true,
    // Summary fields are the live source of truth. Keeping the previously
    // hydrated counters made a newly arrived visit visible in the list while
    // the list badge itself still showed the old total.
    sessions: summary.sessions,
    organizationVisitCount: summary.organizationVisitCount ?? current.organizationVisitCount,
    organizationVisitCountExact: summary.organizationVisitCountExact ?? current.organizationVisitCountExact,
    engagementMs: Math.max(current.engagementMs ?? 0, summary.engagementMs ?? 0),
    journey: current.journey,
    replaySessions: current.replaySessions,
    detailVisitOccurrences: current.detailVisitOccurrences,
    organizationVisitors: current.organizationVisitors,
    organizationAccount: current.organizationAccount,
    evidence: current.evidence,
    network: current.network.networkClass !== "unknown" ? current.network : summary.network,
    location: precisionRank[current.location.precision] >= precisionRank[summary.location.precision] ? current.location : summary.location,
    resolutionStory: current.resolutionStory
  };
}

function fuseLinkedAccount(
  visitor: WorkspaceEntity,
  account: WorkspaceEntity
): WorkspaceEntity {
  const visitorHasCompany = visitor.companyName !== "Consentierter Besucher" && visitor.companyName !== "Unbekanntes Unternehmen";
  const journey = visitor.journey.length ? visitor.journey : account.journey;
  const evidence = [...visitor.evidence, ...account.evidence]
    .filter((entry, index, list) => list.findIndex((candidate) =>
      candidate.family === entry.family && candidate.source === entry.source && candidate.observedAt === entry.observedAt
    ) === index);
  const exactOrganizationVisitCounts = [visitor, account]
    .filter((entity) => entity.organizationVisitCountExact === true)
    .map((entity) => entity.organizationVisitCount ?? 0);
  const exactOrganizationVisitCount = exactOrganizationVisitCounts.length
    ? Math.max(...exactOrganizationVisitCounts)
    : undefined;
  return {
    ...visitor,
    organizationId: visitor.organizationId ?? account.organizationId,
    companyName: visitorHasCompany ? visitor.companyName : account.companyName,
    legalName: visitor.legalName ?? account.legalName,
    domain: visitor.domain ?? account.domain,
    companyLocation: visitor.companyLocation ?? account.companyLocation,
    legalEntityKind: visitor.legalEntityKind === "unknown" ? account.legalEntityKind : visitor.legalEntityKind,
    employeeBand: visitor.employeeBand === "unknown" ? account.employeeBand : visitor.employeeBand,
    confidence: Math.max(visitor.confidence, account.confidence),
    confidenceLabel: account.confidence > visitor.confidence ? account.confidenceLabel : visitor.confidenceLabel,
    targetAccount: visitor.targetAccount || account.targetAccount,
    organizationVisitCount: exactOrganizationVisitCount ?? (Math.max(
      visitor.organizationVisitCount ?? 0,
      account.organizationVisitCount ?? 0
    ) || undefined),
    organizationVisitCountExact: exactOrganizationVisitCounts.length > 0,
    crmStatus: visitor.crmStatus ?? account.crmStatus,
    location: visitor.location.precision === "unknown" ? account.location : visitor.location,
    network: visitor.network.networkClass === "unknown" ? account.network : visitor.network,
    acquisition: visitor.acquisition ?? account.acquisition,
    acquisitionSource: visitor.acquisitionSource ?? account.acquisitionSource,
    journey,
    evidence,
    resolutionStory: [
      ...visitor.resolutionStory,
      {
        label: "Firmen- und Visitor-Signal zusammengeführt",
        detail: "Das consentierte Visitor-Profil ist über einen einmaligen Request-Bezug mit der Firmenaktivität verknüpft. Der Besuch ohne Einwilligung bleibt davon getrennt in der Firmenhistorie sichtbar.",
        status: "complete" as const
      },
      ...account.resolutionStory
    ]
  };
}

function intervalDistanceMs(
  left: { startedAt: string; lastSeenAt: string },
  right: { startedAt: string; lastSeenAt: string }
) {
  const leftStart = new Date(left.startedAt).getTime();
  const leftEnd = new Date(left.lastSeenAt).getTime();
  const rightStart = new Date(right.startedAt).getTime();
  const rightEnd = new Date(right.lastSeenAt).getTime();
  if (![leftStart, leftEnd, rightStart, rightEnd].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  if (leftEnd < rightStart) return rightStart - leftEnd;
  if (rightEnd < leftStart) return leftStart - rightEnd;
  return 0;
}

function companyIdentityKey(entity: WorkspaceEntity) {
  if (entity.organizationId) return `organization:${entity.organizationId}`;
  if (entity.domain) return `domain:${entity.domain.toLowerCase()}`;
  if (classifyActivity(entity) === "recognized") return `company:${entity.companyName.trim().toLowerCase()}`;
  return `entity:${entity.id}`;
}

const DUPLICATE_VISIT_WINDOW_MS = 30 * 60_000;
const CONTINUOUS_VISIT_WINDOW_MS = 5 * 60_000;

export function deduplicateVisitOccurrences(items: WorkspaceEntity[]) {
  return items.map((entity) => {
    if (!entity.visitOccurrences?.length) return entity;
    const canInferContinuousVisitorSession = entity.kind === "visitor" && entity.consent.status === "accepted";
    const ordered = [...entity.visitOccurrences].sort((left, right) => left.startedAt.localeCompare(right.startedAt));
    const merged: NonNullable<WorkspaceEntity["visitOccurrences"]> = [];
    const sessionAliases = new Map<string, string>();
    for (const occurrence of ordered) {
      const duplicateIndex = merged.findIndex((candidate) => {
        if (candidate.id === occurrence.id) return true;
        if (candidate.requestTraceId && occurrence.requestTraceId && candidate.requestTraceId === occurrence.requestTraceId) return true;
        const sameSession = candidate.sessionNumber > 0 && candidate.sessionNumber === occurrence.sessionNumber;
        const distance = intervalDistanceMs(candidate, occurrence);
        return (sameSession && distance <= DUPLICATE_VISIT_WINDOW_MS)
          || (canInferContinuousVisitorSession && distance <= CONTINUOUS_VISIT_WINDOW_MS);
      });
      if (duplicateIndex < 0) {
        merged.push({ ...occurrence });
        continue;
      }
      const current = merged[duplicateIndex];
      const exactOccurrence = current.id === occurrence.id || Boolean(
        current.requestTraceId
        && occurrence.requestTraceId
        && current.requestTraceId === occurrence.requestTraceId
      );
      const mergedEngagementMs = exactOccurrence
        ? Math.max(current.engagementMs ?? 0, occurrence.engagementMs ?? 0)
        : Math.max(0, current.engagementMs ?? 0) + Math.max(0, occurrence.engagementMs ?? 0);
      const mergedEngagementMeasurement = mergedEngagementMs > 0
        ? "measured" as const
        : current.engagementMeasurement === "below_resolution" || occurrence.engagementMeasurement === "below_resolution"
          ? "below_resolution" as const
          : current.engagementMeasurement ?? occurrence.engagementMeasurement;
      sessionAliases.set(occurrence.id, current.id);
      const occurrenceIsLater = occurrence.lastSeenAt >= current.lastSeenAt;
      merged[duplicateIndex] = {
        ...current,
        ...(occurrenceIsLater ? occurrence : {}),
        id: current.id,
        startedAt: current.startedAt < occurrence.startedAt ? current.startedAt : occurrence.startedAt,
        lastSeenAt: current.lastSeenAt > occurrence.lastSeenAt ? current.lastSeenAt : occurrence.lastSeenAt,
        returning: current.returning || occurrence.returning,
        sessionNumber: Math.max(current.sessionNumber, occurrence.sessionNumber),
        engagementMs: mergedEngagementMs,
        ...(mergedEngagementMeasurement ? { engagementMeasurement: mergedEngagementMeasurement } : {})
      };
    }
    const replaySessions = entity.replaySessions?.reduce<NonNullable<WorkspaceEntity["replaySessions"]>>((groups, session) => {
      const canonicalId = sessionAliases.get(session.id) ?? session.id;
      const existingIndex = groups.findIndex((candidate) => candidate.id === canonicalId);
      const sourceSessionIds = [...new Set([
        ...(session.sourceSessionIds ?? []),
        ...(session.replayAvailable && session.replayConsentGranted ? [session.id] : [])
      ])];
      if (existingIndex < 0) {
        groups.push({ ...session, id: canonicalId, sourceSessionIds });
        return groups;
      }
      const current = groups[existingIndex];
      const later = session.endedAt >= current.endedAt ? session : current;
      groups[existingIndex] = {
        ...current,
        title: later.title,
        path: later.path ?? current.path,
        startedAt: current.startedAt < session.startedAt ? current.startedAt : session.startedAt,
        endedAt: current.endedAt > session.endedAt ? current.endedAt : session.endedAt,
        historicalOnly: current.historicalOnly && session.historicalOnly,
        replayAvailable: current.replayAvailable || session.replayAvailable,
        replayConsentGranted: current.replayConsentGranted || session.replayConsentGranted,
        legacyPayloadAvailable: current.legacyPayloadAvailable || session.legacyPayloadAvailable,
        sourceSessionIds: [...new Set([...(current.sourceSessionIds ?? []), ...sourceSessionIds])]
      };
      return groups;
    }, []);
    return {
      ...entity,
      sessions: merged.length,
      journey: entity.journey.map((event) => ({ ...event, sessionId: event.sessionId ? sessionAliases.get(event.sessionId) ?? event.sessionId : event.sessionId })),
      visitOccurrences: merged.sort((left, right) => right.lastSeenAt.localeCompare(left.lastSeenAt)),
      ...(replaySessions ? { replaySessions: replaySessions.sort((left, right) => right.startedAt.localeCompare(left.startedAt)) } : {})
    };
  });
}

function markReturningCompanyOccurrences(items: WorkspaceEntity[]) {
  const occurrenceOrder = new Map<string, Array<{
    entityId: string;
    occurrenceId: string;
    startedAt: string;
    exact: boolean;
    sessionNumber: number;
  }>>();
  items.forEach((entity) => {
    if (!entity.visitOccurrences?.length) return;
    const key = companyIdentityKey(entity);
    const group = occurrenceOrder.get(key) ?? [];
    entity.visitOccurrences.forEach((occurrence) => {
      group.push({
        entityId: entity.id,
        occurrenceId: occurrence.id,
        startedAt: occurrence.startedAt,
        exact: entity.organizationVisitCountExact === true,
        sessionNumber: occurrence.sessionNumber
      });
    });
    occurrenceOrder.set(key, group);
  });
  const returning = new Set<string>();
  const visitNumbers = new Map<string, number>();
  occurrenceOrder.forEach((occurrences) => {
    occurrences
      .sort((left, right) => left.startedAt.localeCompare(right.startedAt))
      .forEach((occurrence, index) => {
        const key = `${occurrence.entityId}:${occurrence.occurrenceId}`;
        // Exact ordinals come from the backend's complete company-wide visit
        // index. A paginated UI can legitimately omit visit 38 while showing
        // visits 36 and 37; shifting the visible ordinals up to the total made
        // visit 36 disappear and was the root cause of the ATP discrepancy.
        const visitNumber = occurrence.exact && occurrence.sessionNumber > 0
          ? occurrence.sessionNumber
          : index + 1;
        visitNumbers.set(key, visitNumber);
        if (visitNumber > 1) returning.add(key);
      });
  });
  return items.map((entity) => entity.visitOccurrences?.length
    ? {
        ...entity,
        visitOccurrences: entity.visitOccurrences.map((occurrence) => ({
          ...occurrence,
          returning: returning.has(`${entity.id}:${occurrence.id}`),
          sessionNumber: visitNumbers.get(`${entity.id}:${occurrence.id}`) ?? occurrence.sessionNumber
        }))
      }
    : entity);
}

export function canonicalWorkspaceEntities(items: WorkspaceEntity[]) {
  items = deduplicateVisitOccurrences(items);
  const visitorsByProfile = new Map(
    items
      .filter((entity) => entity.kind === "visitor" && entity.visitorProfileId)
      .map((entity) => [entity.visitorProfileId!, entity])
  );
  const visitorSessionIdsByProfile = new Map([...visitorsByProfile.entries()].map(([profileId, visitor]) => [
    profileId,
    new Set((visitor.visitOccurrences ?? []).map((occurrence) => occurrence.id))
  ]));
  const accountProjection = new Map<string, WorkspaceEntity | null>();
  for (const account of items.filter((entity) => entity.kind === "account")) {
    const linkedProfileIds = new Set([
      account.linkedVisitorProfileId,
      ...(account.visitOccurrences ?? []).map((occurrence) => occurrence.linkedVisitorProfileId)
    ].filter((value): value is string => Boolean(value)));
    linkedProfileIds.forEach((visitorProfileId) => {
      const visitor = visitorsByProfile.get(visitorProfileId);
      const sameCompanyScope = Boolean(
        !visitor?.organizationId
        || !account.organizationId
        || visitor.organizationId === account.organizationId
      );
      if (visitor && sameCompanyScope) visitorsByProfile.set(visitorProfileId, fuseLinkedAccount(visitor, account));
    });
    if (account.visitOccurrences?.length) {
      const duplicateOccurrenceIds = new Set<string>();
      const duplicateRequestTraceIds = new Set<string>();
      const independentOccurrences = account.visitOccurrences.filter((occurrence) => {
        const linkedProfileId = occurrence.linkedVisitorProfileId;
        const linkedSessionId = occurrence.linkedSessionId;
        const provenDuplicate = Boolean(
          linkedProfileId
          && linkedSessionId
          && visitorSessionIdsByProfile.get(linkedProfileId)?.has(linkedSessionId)
        );
        if (provenDuplicate) {
          duplicateOccurrenceIds.add(occurrence.id);
          if (occurrence.requestTraceId) duplicateRequestTraceIds.add(occurrence.requestTraceId);
        }
        return !provenDuplicate;
      });
      accountProjection.set(account.id, independentOccurrences.length
        ? {
            ...account,
            visitOccurrences: independentOccurrences,
            journey: account.journey.filter((event) =>
              !duplicateOccurrenceIds.has(event.sessionId ?? "")
              && !duplicateRequestTraceIds.has(event.id)
            )
          }
        : null);
    } else {
      accountProjection.set(
        account.id,
        account.linkedVisitorProfileId && visitorsByProfile.has(account.linkedVisitorProfileId) ? null : account
      );
    }
  }
  return markReturningCompanyOccurrences(items
    .map((entity) => {
      if (entity.kind === "visitor" && entity.visitorProfileId) return visitorsByProfile.get(entity.visitorProfileId) ?? entity;
      if (entity.kind === "account") return accountProjection.get(entity.id) ?? null;
      return entity;
    })
    .filter((entity): entity is WorkspaceEntity => Boolean(entity)));
}

export function applyHydratedWorkspaceEntity(items: WorkspaceEntity[], id: string, detail: WorkspaceEntity) {
  const summary = items.find((item) => item.id === id);
  const hydratedDetail = summary
    ? {
        ...detail,
        // List summaries carry the current presence lease. Detail endpoints
        // intentionally focus on history and may omit or lag these volatile
        // fields; opening a detail must therefore never end a live visit.
        live: summary.live,
        liveUntil: summary.liveUntil,
        currentVisitId: summary.currentVisitId ?? detail.currentVisitId,
        currentPath: summary.currentPath ?? detail.currentPath,
        lastSeenAt: summary.lastSeenAt > detail.lastSeenAt ? summary.lastSeenAt : detail.lastSeenAt,
        sessions: summary.sessions,
        organizationVisitCount: detail.organizationVisitCountExact
          ? detail.organizationVisitCount
          : summary.organizationVisitCountExact
            ? summary.organizationVisitCount
            : Math.max(
              detail.organizationVisitCount ?? 0,
              summary.organizationVisitCount ?? 0
            ) || undefined,
        organizationVisitCountExact: summary.organizationVisitCountExact || detail.organizationVisitCountExact,
        // Detail endpoints contain the complete profile history. The activity
        // list is independently paginated, so replacing its loaded windows
        // here would create rows merely because a detail was opened.
        detailVisitOccurrences: detail.visitOccurrences,
        visitOccurrences: summary.visitOccurrences
      }
    : detail;
  const canonical = canonicalWorkspaceEntities(items.map((item) => item.id === id ? hydratedDetail : item));
  const selected = canonical.find((item) => item.id === id)
    ?? canonical.find((item) => detail.visitorProfileId && item.visitorProfileId === detail.visitorProfileId)
    ?? canonical.find((item) => mapActivityIdentityKey(item) === mapActivityIdentityKey(detail));
  return { items: canonical, selectedId: selected?.id ?? "" };
}

function readTargetAccountOverrides(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(TARGET_ACCOUNTS_KEY) || "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

function readSavedFilters(siteId: string): SavedVisitorFilter[] {
  try {
    const all = JSON.parse(localStorage.getItem(SAVED_FILTERS_KEY) || "{}") as Record<string, SavedVisitorFilter[]>;
    return Array.isArray(all[siteId]) ? all[siteId].slice(0, 20) : [];
  } catch {
    return [];
  }
}

function normalizeActivityClasses(value: unknown): ActivityClass[] {
  if (!Array.isArray(value)) return ["visitor", "recognized"];
  const allowed = new Set<ActivityClass>(["visitor", "recognized", "bot"]);
  const selected = new Set(value
    .map((item) => item === "lead" ? "recognized" : item)
    .filter((item): item is ActivityClass => typeof item === "string" && allowed.has(item as ActivityClass)));
  if (!selected.size || !selected.has("recognized")) {
    selected.add("visitor");
    selected.add("recognized");
  }
  return (["visitor", "recognized", "bot"] as ActivityClass[]).filter((activityClass) => selected.has(activityClass));
}

function writeSavedFilters(siteId: string, views: SavedVisitorFilter[]) {
  let all: Record<string, SavedVisitorFilter[]> = {};
  try {
    all = JSON.parse(localStorage.getItem(SAVED_FILTERS_KEY) || "{}") as Record<string, SavedVisitorFilter[]>;
  } catch {
    all = {};
  }
  all[siteId] = views.slice(0, 20);
  localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(all));
}

function readActiveFilters(siteId: string): VisitorFilterSnapshot | null {
  try {
    const all = JSON.parse(localStorage.getItem(ACTIVE_FILTERS_KEY) || "{}") as Record<string, VisitorFilterSnapshot>;
    return all[siteId] && typeof all[siteId] === "object" ? all[siteId] : null;
  } catch {
    return null;
  }
}

function writeActiveFilters(siteId: string, filters: VisitorFilterSnapshot) {
  let all: Record<string, VisitorFilterSnapshot> = {};
  try {
    all = JSON.parse(localStorage.getItem(ACTIVE_FILTERS_KEY) || "{}") as Record<string, VisitorFilterSnapshot>;
  } catch {
    all = {};
  }
  all[siteId] = filters;
  localStorage.setItem(ACTIVE_FILTERS_KEY, JSON.stringify(all));
}

function leadFitWeightsFromFields(fields: SettingsFieldsResponse["fields"]): LeadFitWeights {
  const number = (id: string, fallback: number) => {
    const raw = fields.find((field) => field.id === id)?.value;
    const value = typeof raw === "string" ? Number(raw) : Number.NaN;
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : fallback;
  };
  return {
    company: number("leadFitCompanyWeight", defaultLeadFitWeights.company),
    confidence: number("leadFitConfidenceWeight", defaultLeadFitWeights.confidence),
    targetAccount: number("leadFitTargetAccountWeight", defaultLeadFitWeights.targetAccount),
    companySize: number("leadFitCompanySizeWeight", defaultLeadFitWeights.companySize),
    buyingIntent: number("leadFitBuyingIntentWeight", defaultLeadFitWeights.buyingIntent)
  };
}

export const useWorkspaceStore = defineStore("workspace", () => {
  const targetAccountOverrides = ref(readTargetAccountOverrides());
  const siteId = ref(import.meta.env.VITE_DEFAULT_SITE_ID || (firebaseConfigured ? "" : "moonshot-demo"));
  const siteName = ref(firebaseConfigured ? "Site wird geladen" : "Moonshot Demo Site");
  const availableSites = ref<SiteDirectoryItem[]>(firebaseConfigured ? [] : [{ id: "moonshot-demo", name: "Moonshot Demo Site", environment: "development", setupStatus: "ready", setupStep: "complete", installationMode: "one_tag", collectorMode: "managed", publicTagId: "123456789012345678901234", verificationSummary: { browser: "verified", consent: "interface_verified", edge: "verified" } }]);
  const organizations = ref<OrganizationDirectoryItem[]>(firebaseConfigured ? [] : [{ id: "demo", name: "Moonshot Demo", roles: ["admin"], active: true }]);
  const activeOrganizationName = computed(() => organizations.value.find((organization) => organization.active)?.name ?? "Organisation");
  const canViewQuality = computed(() => canAccessQuality(organizations.value.find(organization => organization.active)?.roles ?? []));
  const entities = ref<WorkspaceEntity[]>((firebaseConfigured ? [] : demoEntities).map((entity) => ({
    ...entity,
    targetAccount: targetAccountOverrides.value[entity.id] ?? entity.targetAccount
  })));
  const requestTraces = ref<RequestTraceView[]>(firebaseConfigured ? [] : demoTraces);
  const activityHistory = ref<RequestTraceView[]>([]);
  const activityHistoryHasMore = ref(false);
  const activityHistoryCursor = ref<string | null>(null);
  let activityHistoryRequest = 0;
  let activityHistoryAvailable = false;
  const statistics = ref<DashboardStatistics>(firebaseConfigured ? emptyStatistics : demoStatistics);
  const connectorHealth = ref<ConnectorHealth>({
    state: firebaseConfigured ? "no_recent_traffic" : "healthy",
    keyConfigured: !firebaseConfigured,
    graphReleaseId: firebaseConfigured ? null : "demo-graph",
    graphExpiresAt: null,
    graphExpiresSoon: false,
    graphRemainingHours: null,
    lastTraceAt: null,
    lastSignalAt: null,
    lastGraphReleaseId: null,
    lastConnectorVersion: null,
          requiredConnectorVersion: "2.28.0",
    connectorOutdated: false,
    graphMismatch: false
  });
  const statisticsDays = ref<7 | 30 | 90>(30);
  const mode = ref<WorkspaceMode>("all");
  const activeDetailTab = ref<DetailTab>("map");
  const selectedId = ref(firebaseConfigured ? "" : demoEntities.find((item) => item.kind === "account")?.id ?? "");
  const selectedOrganizationContextId = ref("");
  const selectedTraceId = ref(demoTraces[0]?.requestTraceId ?? "");
  const search = ref("");
  const liveOnly = ref(false);
  const consentFilter = ref<ConsentStatus | "all">("all");
  const confidenceFilter = ref<"all" | "published" | "candidate" | "unknown" | "suppressed">("all");
  const intentFilter = ref("all");
  const acquisitionFilter = ref("all");
  const confidenceMin = ref(0);
  const countryFilter = ref("all");
  const countryFilters = ref<string[]>([]);
  const mapEntityKeys = ref<string[]>([]);
  const activityClassFilters = ref<ActivityClass[]>(["visitor", "recognized"]);
  const deviceFilters = ref<DeviceClassFilter[]>([]);
  const datePreset = ref<DatePreset>("today");
  const initialDateRange = dateRangeForPreset("today");
  const dateFrom = ref(initialDateRange.from);
  const dateTo = ref(initialDateRange.to);
  const frequencyFilter = ref<VisitFrequencyFilter>("all");
  const revealFilter = ref<EntityChoiceFilter>("all");
  const targetFilter = ref<EntityChoiceFilter>("all");
  const crmFilter = ref<EntityChoiceFilter>("all");
  const sort = ref<"recent" | "confidence" | "sessions" | "intent" | "lead_fit">("recent");
  const activitySummary = ref(localStorage.getItem(ACTIVITY_SUMMARY_KEY) === "true");
  const leadFitWeights = ref<LeadFitWeights>({ ...defaultLeadFitWeights });
  const savedVisitorFilters = ref<SavedVisitorFilter[]>(readSavedFilters(siteId.value));
  const traceStatus = ref<RequestTraceStatus | "all">("all");
  const listCollapsed = ref(localStorage.getItem(LIST_COLLAPSED_KEY) === "true");
  const traceLoading = ref(false);
  const traceError = ref("");
  const traceNextCursor = ref<string | null>(null);
  const traceHasMore = ref(false);
  const traceScanLimitReached = ref(false);
  const traceQueryFilters = ref<TraceQueryFilters>({});
  const accountNextCursor = ref<string | null>(null);
  const accountHasMore = ref(false);
  const visitorNextCursor = ref<string | null>(null);
  const visitorHasMore = ref(false);
  const entityPageLoading = ref(false);
  const workspaceLoading = ref(firebaseConfigured);
  const workspaceError = ref("");
  const lastRefreshedAt = ref("");
  const liveSyncStatus = ref<"demo" | "off" | "connecting" | "live" | "error">(firebaseConfigured ? "off" : "demo");
  const liveSyncError = ref("");
  let liveUnsubscribe: Unsubscribe | undefined;
  let liveRevision = "";
  // An old tenant/site request may finish after a switch. Never let that
  // response (or its finally block) mutate the new workspace.
  let contextEpoch = 0;
  let refreshingEpoch: number | undefined;
  const organizationSwitching = ref(false);
  let organizationSwitchTail: Promise<void> = Promise.resolve();
  let pendingSilentRefresh = false;
  let pendingLiveKind: LiveRevisionKind | undefined;
  let livePollTimer: number | undefined;
  let liveAgeTimer: number | undefined;
  const entityDetailsLoading = new Set<string>();
  const pendingEntityDetailRefresh = new Set<string>();

  function resetWorkspaceContext() {
    ++contextEpoch;
    stopLiveUpdates();
    refreshingEpoch = undefined;
    pendingSilentRefresh = false;
    pendingLiveKind = undefined;
    entityDetailsLoading.clear();
    pendingEntityDetailRefresh.clear();
    searchPageExpansionActive = false;
    if (searchPageExpansionTimer !== undefined) window.clearTimeout(searchPageExpansionTimer);
    selectedId.value = "";
    selectedTraceId.value = "";
    selectedOrganizationContextId.value = "";
    entities.value = [];
    requestTraces.value = [];
    activityHistory.value = [];
    activityHistoryHasMore.value = false;
    activityHistoryCursor.value = null;
    ++activityHistoryRequest;
    activityHistoryAvailable = false;
    statistics.value = { ...emptyStatistics };
    connectorHealth.value = { state: "no_recent_traffic", keyConfigured: false, graphReleaseId: null,
      graphExpiresAt: null, graphExpiresSoon: false, graphRemainingHours: null, lastTraceAt: null,
      lastSignalAt: null, lastGraphReleaseId: null, lastConnectorVersion: null, requiredConnectorVersion: "2.28.0",
      connectorOutdated: false, graphMismatch: false };
    mapEntityKeys.value = [];
    traceNextCursor.value = accountNextCursor.value = visitorNextCursor.value = null;
    traceHasMore.value = accountHasMore.value = visitorHasMore.value = false;
    traceScanLimitReached.value = false;
    traceQueryFilters.value = {};
    traceStatus.value = "all";
    traceError.value = workspaceError.value = "";
    traceLoading.value = entityPageLoading.value = false;
    lastRefreshedAt.value = "";
    leadFitWeights.value = { ...defaultLeadFitWeights };
    setActiveLeadFitWeights(leadFitWeights.value);
  }

  function filteredEntityCollection(
    source: WorkspaceEntity[],
    options: {
      ignoreMode?: boolean;
      ignoreActivityClass?: boolean;
      ignoreCountry?: boolean;
      /** The map keeps unselected markers visible (dimmed) while the list shows only the selection. */
      ignoreMapSelection?: boolean;
    } = {}
  ) {
    const query = search.value.trim().toLowerCase();
    const isReturningActivity = (item: WorkspaceEntity) => item.activityOccurrenceId
      ? Boolean(item.activityReturning)
      : item.sessions > 1;
    return source
      .filter((item) => options.ignoreMode || mode.value === "all" || isIdentifiedEntity(item))
      .filter((item) => !liveOnly.value || item.live)
      .filter((item) => consentFilter.value === "all" || item.consent.status === consentFilter.value)
      .filter((item) => intentFilter.value === "all" || item.intentCategory === intentFilter.value)
      .filter((item) => acquisitionFilter.value === "all"
        || acquisitionChannel(item.acquisition, item.acquisitionSource) === acquisitionFilter.value)
      .filter((item) => item.confidence * 100 >= confidenceMin.value)
      .filter((item) => options.ignoreActivityClass || activityClassFilters.value.includes(classifyActivity(item)))
      .filter((item) => options.ignoreMapSelection || !mapEntityKeys.value.length || mapEntityKeys.value.includes(mapActivityIdentityKey(item)))
      .filter((item) => !deviceFilters.value.length || deviceFilters.value.includes(normalizedDeviceClass(item) as DeviceClassFilter))
      .filter((item) => {
        if (options.ignoreCountry) return true;
        const selectedCountries = countryFilters.value.length
          ? countryFilters.value
          : countryFilter.value === "all" ? [] : [countryFilter.value];
        return !selectedCountries.length || selectedCountries.includes(activityLocation(item).countryCode);
      })
      .filter((item) => {
        const seen = new Date(item.lastSeenAt).getTime();
        if (dateFrom.value && seen < new Date(`${dateFrom.value}T00:00:00`).getTime()) return false;
        if (dateTo.value && seen > new Date(`${dateTo.value}T23:59:59.999`).getTime()) return false;
        return true;
      })
      .filter((item) => frequencyFilter.value === "all"
        || (frequencyFilter.value === "new" ? !isReturningActivity(item) : isReturningActivity(item)))
      .filter((item) => revealFilter.value === "all" || (revealFilter.value === "yes" ? Boolean(item.revealedEmail) : !item.revealedEmail))
      .filter((item) => targetFilter.value === "all" || (targetFilter.value === "yes" ? item.targetAccount : !item.targetAccount))
      .filter((item) => crmFilter.value === "all" || (crmFilter.value === "yes" ? Boolean(item.crmStatus) : !item.crmStatus))
      .filter((item) => {
        if (confidenceFilter.value === "all") return true;
        if (confidenceFilter.value === "published") return item.confidence >= 0.52 || item.confidenceLabel === "verified";
        if (confidenceFilter.value === "candidate") return item.confidence > 0 && item.confidence < 0.52;
        if (confidenceFilter.value === "unknown") return item.confidence <= 0;
        return item.reviewStatus === "suppressed";
      })
      .filter((item) => !query || [
        item.companyName,
        item.domain,
        item.location.label,
        item.activityOriginLabel,
        item.intentCategory,
        item.currentPath,
        item.visitorLabel
      ].filter(Boolean).some((value) => value!.toLowerCase().includes(query)))
      .sort((left, right) => {
        if (sort.value === "confidence") return right.confidence - left.confidence;
        if (sort.value === "sessions") return (right.activitySearchVisitCount || right.organizationVisitCount || right.sessions) - (left.activitySearchVisitCount || left.organizationVisitCount || left.sessions);
        if (sort.value === "intent") return deriveEntityInsights(right, leadFitWeights.value).buyingIntentScore - deriveEntityInsights(left, leadFitWeights.value).buyingIntentScore;
        if (sort.value === "lead_fit") return deriveEntityInsights(right, leadFitWeights.value).leadFitScore - deriveEntityInsights(left, leadFitWeights.value).leadFitScore;
        return right.lastSeenAt.localeCompare(left.lastSeenAt);
      });
  }

  const filteredEntities = computed(() => filteredEntityCollection(entities.value));
  const mapEntities = computed(() => filteredEntityCollection(entities.value, { ignoreCountry: true, ignoreMapSelection: true }));
  const expandedActivityEntities = computed(() => deduplicateExpandedActivities(expandActivityOccurrences(entities.value)));
  const filteredActivityEntities = computed(() => {
    const filtered = filteredEntityCollection(expandedActivityEntities.value);
    const aggregated = search.value.trim() || sort.value !== "recent"
      ? aggregateActivityResults(filtered)
      : activitySummary.value
        ? aggregateActivityResultsByRecency(filtered)
      : aggregateCompanySearchResults(filtered, search.value);
    return [...aggregated].sort((left, right) => {
      if (sort.value === "confidence") return right.confidence - left.confidence;
      if (sort.value === "sessions") return (right.activitySearchVisitCount || right.organizationVisitCount || right.sessions) - (left.activitySearchVisitCount || left.organizationVisitCount || left.sessions);
      if (sort.value === "intent") return deriveEntityInsights(right, leadFitWeights.value).buyingIntentScore - deriveEntityInsights(left, leadFitWeights.value).buyingIntentScore;
      if (sort.value === "lead_fit") return deriveEntityInsights(right, leadFitWeights.value).leadFitScore - deriveEntityInsights(left, leadFitWeights.value).leadFitScore;
      return right.lastSeenAt.localeCompare(left.lastSeenAt);
    });
  });
  const activitySummaryForced = computed(() => Boolean(search.value.trim()) || sort.value !== "recent");
  const activitySummaryActive = computed(() => activitySummary.value || activitySummaryForced.value);
  const recognizedCompaniesOnly = computed(() =>
    activityClassFilters.value.includes("recognized") && !activityClassFilters.value.includes("visitor")
  );
  const botsVisible = computed(() => activityClassFilters.value.includes("bot"));
  // Facet and tab counters must describe the same filtered time slice as the
  // visible list. They only ignore the two dimensions they summarize
  // themselves (workspace mode and activity class).
  const activityFacetEntities = computed(() => filteredEntityCollection(
    expandedActivityEntities.value,
    { ignoreMode: true, ignoreActivityClass: true }
  ));

  // Never render a different entity merely because a realtime refresh is
  // between its summary and detail response. Selection changes are handled
  // explicitly by ensureVisibleSelection/applyHydratedWorkspaceEntity.
  const selectedEntity = computed(() =>
    entities.value.find((item) => item.id === selectedId.value) ?? null
  );
  const filteredTraces = computed(() => requestTraces.value.filter((trace) => traceStatus.value === "all" || trace.status === traceStatus.value));
  const selectedTrace = computed(() => requestTraces.value.find((trace) => trace.requestTraceId === selectedTraceId.value) ?? filteredTraces.value[0] ?? null);

  function ensureVisibleSelection(options: { hydrate?: boolean } = {}) {
    const visible = filteredEntityCollection(entities.value);
    const current = visible.find((item) => item.id === selectedId.value);
    const next = current ?? visible[0];
    const changed = selectedId.value !== (next?.id ?? "");
    selectedId.value = next?.id ?? "";
    selectedOrganizationContextId.value = next?.organizationId ?? "";
    if (next && options.hydrate !== false && (changed || next.detailsLoaded === false)) {
      void hydrateEntity(next.id);
    }
    return selectedId.value;
  }

  function setMode(next: WorkspaceMode) {
    mode.value = next;
    ensureVisibleSelection();
  }

  function setDatePreset(next: DatePreset) {
    datePreset.value = next;
    if (next === "custom") return;
    const range = dateRangeForPreset(next);
    dateFrom.value = range.from;
    dateTo.value = range.to;
  }

  function toggleActivityClass(value: ActivityClass) {
    const selected = new Set(activityClassFilters.value);
    if (value === "recognized") {
      selected.add("recognized");
      if (selected.has("visitor")) selected.delete("visitor");
      else selected.add("visitor");
    } else if (value === "bot") {
      if (selected.has("bot")) selected.delete("bot");
      else selected.add("bot");
    } else {
      selected.add("visitor");
      selected.add("recognized");
    }
    activityClassFilters.value = (["visitor", "recognized", "bot"] as ActivityClass[])
      .filter((activityClass) => selected.has(activityClass));
  }

  function toggleDevice(value: DeviceClassFilter) {
    const selected = new Set(deviceFilters.value);
    if (selected.has(value)) selected.delete(value);
    else selected.add(value);
    deviceFilters.value = [...selected];
  }

  function toggleCountry(value: string) {
    const selected = new Set(countryFilters.value);
    if (selected.has(value)) selected.delete(value);
    else selected.add(value);
    countryFilters.value = [...selected];
    countryFilter.value = "all";
  }

  function selectEntity(id: string, organizationId = "") {
    selectedId.value = id;
    selectedOrganizationContextId.value = organizationId
      || entities.value.find((entity) => entity.id === id)?.organizationId
      || "";
    void hydrateEntity(id, { organizationId: selectedOrganizationContextId.value });
  }

  async function hydrateEntity(id: string, options: { force?: boolean; organizationId?: string } = {}) {
    if (!firebaseConfigured) return;
    const epoch = contextEpoch;
    if (entityDetailsLoading.has(id)) {
      if (options.force) pendingEntityDetailRefresh.add(id);
      return;
    }
    const entity = entities.value.find((item) => item.id === id);
    if (!entity || (!options.force && entity.detailsLoaded !== false)) return;
    const organizationId = options.organizationId
      || (selectedId.value === id ? selectedOrganizationContextId.value : "")
      || entity.organizationId
      || "";
    const accountPath = organizationId
      ? `/admin/sites/${encodeURIComponent(siteId.value)}/accounts/${encodeURIComponent(organizationId)}`
      : "";
    const visitorPath = entity.kind === "visitor" && entity.visitorProfileId
      ? `/admin/sites/${encodeURIComponent(siteId.value)}/visitors/${encodeURIComponent(entity.visitorProfileId)}`
      : "";
    if (!accountPath && !visitorPath) return;
    entityDetailsLoading.add(id);
    try {
      const [visitorResult, accountResult] = await Promise.allSettled([
        visitorPath ? apiFetch<WorkspaceEntity>(visitorPath) : Promise.resolve(undefined),
        accountPath ? apiFetch<WorkspaceEntity>(accountPath) : Promise.resolve(undefined)
      ]);
      if (epoch !== contextEpoch) return;
      const visitorDetail = visitorResult.status === "fulfilled" ? visitorResult.value : undefined;
      const accountDetail = accountResult.status === "fulfilled" ? accountResult.value : undefined;
      if (!visitorDetail && !accountDetail) {
        const failure = visitorResult.status === "rejected" ? visitorResult.reason : accountResult.status === "rejected" ? accountResult.reason : undefined;
        throw failure instanceof Error ? failure : new Error("Details konnten nicht geladen werden.");
      }
      const accountScopedVisitor = visitorDetail && accountDetail
        ? accountDetail.organizationVisitors?.find((visitor) =>
            visitor.visitorProfileId === visitorDetail.visitorProfileId
          )
        : undefined;
      const scopedVisitorDetail = accountScopedVisitor ?? visitorDetail;
      const detail = scopedVisitorDetail && accountDetail
        ? {
            ...scopedVisitorDetail,
            organizationId: accountDetail.organizationId || scopedVisitorDetail.organizationId,
            companyName: accountDetail.companyName || scopedVisitorDetail.companyName,
            legalName: accountDetail.legalName || scopedVisitorDetail.legalName,
            domain: accountDetail.domain || scopedVisitorDetail.domain,
            companyLocation: accountDetail.companyLocation || scopedVisitorDetail.companyLocation,
            logoUrl: accountDetail.logoUrl || scopedVisitorDetail.logoUrl,
            website: accountDetail.website || scopedVisitorDetail.website,
            industry: accountDetail.industry || scopedVisitorDetail.industry,
            description: accountDetail.description || scopedVisitorDetail.description,
            employeeCount: accountDetail.employeeCount ?? scopedVisitorDetail.employeeCount,
            annualRevenue: accountDetail.annualRevenue ?? scopedVisitorDetail.annualRevenue,
            revenueCurrency: accountDetail.revenueCurrency || scopedVisitorDetail.revenueCurrency,
            lei: accountDetail.lei || scopedVisitorDetail.lei,
            registrationNumber: accountDetail.registrationNumber || scopedVisitorDetail.registrationNumber,
            vatId: accountDetail.vatId || scopedVisitorDetail.vatId,
            legalForm: accountDetail.legalForm || scopedVisitorDetail.legalForm,
            companyDataCheckedAt: accountDetail.companyDataCheckedAt || scopedVisitorDetail.companyDataCheckedAt,
            companyDataUpdatedAt: accountDetail.companyDataUpdatedAt || scopedVisitorDetail.companyDataUpdatedAt,
            companyDataEnrichmentVersion: accountDetail.companyDataEnrichmentVersion || scopedVisitorDetail.companyDataEnrichmentVersion,
            companyDataSourceResults: accountDetail.companyDataSourceResults || scopedVisitorDetail.companyDataSourceResults,
            companyDataSources: accountDetail.companyDataSources?.length ? accountDetail.companyDataSources : scopedVisitorDetail.companyDataSources,
            companyDataProvenance: accountDetail.companyDataProvenance?.length ? accountDetail.companyDataProvenance : scopedVisitorDetail.companyDataProvenance,
            organizationVisitCount: Math.max(
              accountDetail.organizationVisitCount ?? 0,
              scopedVisitorDetail.organizationVisitCount ?? 0
            ) || undefined,
            address: accountDetail.address || scopedVisitorDetail.address,
            contacts: accountDetail.contacts?.length ? accountDetail.contacts : scopedVisitorDetail.contacts,
            legalEntityKind: accountDetail.legalEntityKind !== "unknown" ? accountDetail.legalEntityKind : scopedVisitorDetail.legalEntityKind,
            employeeBand: accountDetail.employeeBand !== "unknown" ? accountDetail.employeeBand : scopedVisitorDetail.employeeBand,
            organizationVisitors: accountDetail.organizationVisitors,
            organizationAccount: accountDetail
          }
        : scopedVisitorDetail ?? accountDetail;
      if (!detail) return;
      const hydrated = applyHydratedWorkspaceEntity(entities.value, id, detail);
      entities.value = hydrated.items;
      if (selectedId.value === id && hydrated.selectedId) selectedId.value = hydrated.selectedId;
    } catch (error) {
      if (epoch === contextEpoch) workspaceError.value = error instanceof Error ? error.message : "Details konnten nicht geladen werden.";
    } finally {
      if (epoch === contextEpoch) entityDetailsLoading.delete(id);
      if (epoch === contextEpoch && pendingEntityDetailRefresh.delete(id)) {
        queueMicrotask(() => { if (epoch === contextEpoch) void hydrateEntity(id, { force: true }); });
      }
    }
  }

  async function enrichOrganization(id: string) {
    const epoch = contextEpoch;
    const entity = entities.value.find((item) => item.id === id);
    if (!entity?.organizationId) throw new Error("Für diese Aktivität ist keine Organisation verknüpft.");
    const response = await apiFetch<{ result: CompanyEnrichmentResult; account: WorkspaceEntity }>(
      `/admin/sites/${encodeURIComponent(siteId.value)}/accounts/${encodeURIComponent(entity.organizationId)}/enrich`,
      { method: "POST", body: "{}" }
    );
    if (epoch === contextEpoch) await hydrateEntity(id, { force: true });
    return response.result;
  }

  async function loadMoreEntities() {
    if (!firebaseConfigured || entityPageLoading.value) return;
    const epoch = contextEpoch;
    const pages = [
      { resource: "accounts" as const, cursor: accountNextCursor.value, hasMore: accountHasMore.value },
      { resource: "visitors" as const, cursor: visitorNextCursor.value, hasMore: visitorHasMore.value }
    ].filter((page) => page.cursor && page.hasMore);
    if (!pages.length) {
      if (!activityHistoryHasMore.value) return;
      entityPageLoading.value = true;
      try { await refreshActivityHistory(true); } finally { if (epoch === contextEpoch) entityPageLoading.value = false; }
      return;
    }
    entityPageLoading.value = true;
    try {
      const loaded = await Promise.all(pages.map(async (request) => ({
        resource: request.resource,
        page: await apiFetch<EntityPageResponse>(`/admin/sites/${encodeURIComponent(siteId.value)}/${request.resource}?limit=250&cursor=${encodeURIComponent(request.cursor!)}`)
      })));
      if (epoch !== contextEpoch) return;
      const merged = new Map(entities.value.map((entity) => [entity.id, entity]));
      loaded.forEach(({ page }) => page.items.forEach((entity) => {
        const current = merged.get(entity.id);
        merged.set(entity.id, current ? mergeWorkspaceEntitySummary(current, entity) : entity);
      }));
      entities.value = canonicalWorkspaceEntities([...merged.values()]);
      loaded.forEach(({ resource, page }) => {
        if (resource === "accounts") {
          accountNextCursor.value = page.nextCursor;
          accountHasMore.value = page.hasMore;
        } else {
          visitorNextCursor.value = page.nextCursor;
          visitorHasMore.value = page.hasMore;
        }
      });
      if (activityHistoryHasMore.value) await refreshActivityHistory(true);
    } catch (error) {
      if (epoch === contextEpoch) workspaceError.value = error instanceof Error ? error.message : "Weitere Aktivitäten konnten nicht geladen werden.";
    } finally {
      if (epoch === contextEpoch) entityPageLoading.value = false;
    }
  }

  let searchPageExpansionTimer: number | undefined;
  let searchPageExpansionActive = false;
  async function expandEntityPagesForSearch() {
    if (searchPageExpansionActive || !firebaseConfigured || !search.value.trim()) return;
    searchPageExpansionActive = true;
    const epoch = contextEpoch;
    try {
      while (epoch === contextEpoch && search.value.trim() && (accountHasMore.value || visitorHasMore.value || activityHistoryHasMore.value)) {
        const previousCursors = `${accountNextCursor.value ?? ""}|${visitorNextCursor.value ?? ""}|${activityHistoryCursor.value ?? ""}`;
        await loadMoreEntities();
        const nextCursors = `${accountNextCursor.value ?? ""}|${visitorNextCursor.value ?? ""}|${activityHistoryCursor.value ?? ""}`;
        if (previousCursors === nextCursors) break;
      }
    } finally {
      if (epoch === contextEpoch) searchPageExpansionActive = false;
    }
  }
  function scheduleSearchPageExpansion() {
    if (searchPageExpansionTimer !== undefined) window.clearTimeout(searchPageExpansionTimer);
    if (!search.value.trim() || (!accountHasMore.value && !visitorHasMore.value && !activityHistoryHasMore.value)) return;
    searchPageExpansionTimer = window.setTimeout(() => void expandEntityPagesForSearch(), 250);
  }
  watch([search, accountHasMore, visitorHasMore, activityHistoryHasMore], scheduleSearchPageExpansion);

  /**
   * The map shows every entry of the selected period, not only the pages the
   * list has scrolled to. After the first paint, the remaining pages of the
   * period load in the background. Accounts and visitors are ordered by
   * lastSeenAt, so loading stops once the oldest loaded entry predates the
   * period start; the page cap bounds "all time".
   */
  const mapCoverageLoading = ref(false);
  const MAP_COVERAGE_MAX_PAGES = 8;
  let mapCoverageTimer: number | undefined;
  const pageCursors = () => `${accountNextCursor.value ?? ""}|${visitorNextCursor.value ?? ""}|${activityHistoryCursor.value ?? ""}`;
  function periodLoaded() {
    if (!accountHasMore.value && !visitorHasMore.value && !activityHistoryHasMore.value) return true;
    if (!dateFrom.value || activityHistoryHasMore.value) return false;
    const start = new Date(`${dateFrom.value}T00:00:00`).getTime();
    const oldest = (kind: WorkspaceEntity["kind"]) => Math.min(...entities.value
      .filter((entity) => entity.kind === kind).map((entity) => Date.parse(entity.lastSeenAt)).filter(Number.isFinite));
    return (!accountHasMore.value || oldest("account") < start) && (!visitorHasMore.value || oldest("visitor") < start);
  }
  async function expandEntityPagesForMap() {
    if (mapCoverageLoading.value || !firebaseConfigured) return;
    const epoch = contextEpoch;
    mapCoverageLoading.value = true;
    try {
      for (let page = 0; page < MAP_COVERAGE_MAX_PAGES && epoch === contextEpoch && !periodLoaded(); page += 1) {
        const before = pageCursors();
        await loadMoreEntities();
        if (pageCursors() === before) break;
        // Yield between pages so scrolling and the map stay responsive.
        await new Promise((resolve) => window.setTimeout(resolve, 150));
      }
    } finally {
      if (epoch === contextEpoch) mapCoverageLoading.value = false;
    }
  }
  function scheduleMapCoverage() {
    if (mapCoverageTimer !== undefined) window.clearTimeout(mapCoverageTimer);
    if (!firebaseConfigured || workspaceLoading.value) return;
    mapCoverageTimer = window.setTimeout(() => void expandEntityPagesForMap(), 400);
  }
  watch([workspaceLoading, dateFrom, dateTo, activityHistoryHasMore], scheduleMapCoverage);

  function toggleList() {
    listCollapsed.value = !listCollapsed.value;
    localStorage.setItem(LIST_COLLAPSED_KEY, String(listCollapsed.value));
  }

  function toggleActivitySummary() {
    if (activitySummaryForced.value) return;
    activitySummary.value = !activitySummary.value;
    localStorage.setItem(ACTIVITY_SUMMARY_KEY, String(activitySummary.value));
  }

  function currentVisitorFilterSnapshot(): VisitorFilterSnapshot {
    return {
      mode: mode.value,
      search: search.value,
      liveOnly: liveOnly.value,
      consent: consentFilter.value,
      confidence: confidenceFilter.value,
      intent: intentFilter.value,
      acquisition: acquisitionFilter.value,
      confidenceMin: confidenceMin.value,
      country: countryFilter.value,
      countries: [...countryFilters.value],
      activityClasses: [...activityClassFilters.value],
      devices: [...deviceFilters.value],
      datePreset: datePreset.value,
      dateFrom: dateFrom.value,
      dateTo: dateTo.value,
      frequency: frequencyFilter.value,
      reveal: "all",
      target: targetFilter.value,
      crm: "all",
      sort: sort.value
    };
  }

  function applyVisitorFilterSnapshot(filters: VisitorFilterSnapshot) {
    mapEntityKeys.value = [];
    mode.value = filters.mode;
    search.value = filters.search;
    liveOnly.value = filters.liveOnly;
    consentFilter.value = filters.consent;
    confidenceFilter.value = filters.confidence;
    intentFilter.value = filters.intent;
    acquisitionFilter.value = filters.acquisition ?? "all";
    confidenceMin.value = Math.min(100, Math.max(0, Number(filters.confidenceMin) || 0));
    countryFilter.value = filters.country;
    countryFilters.value = Array.isArray(filters.countries)
      ? filters.countries
      : filters.country && filters.country !== "all" ? [filters.country] : [];
    activityClassFilters.value = normalizeActivityClasses(filters.activityClasses);
    deviceFilters.value = Array.isArray(filters.devices) ? filters.devices : [];
    datePreset.value = filters.datePreset ?? "custom";
    dateFrom.value = filters.dateFrom;
    dateTo.value = filters.dateTo;
    frequencyFilter.value = filters.frequency;
    revealFilter.value = "all";
    targetFilter.value = ["all", "yes", "no"].includes(filters.target) ? filters.target : "all";
    crmFilter.value = "all";
    sort.value = ["recent", "confidence", "sessions", "intent", "lead_fit"].includes(filters.sort) ? filters.sort : "recent";
  }

  function persistVisitorFilters() {
    writeActiveFilters(siteId.value, currentVisitorFilterSnapshot());
  }

  function restoreVisitorFilters() {
    const saved = readActiveFilters(siteId.value);
    if (!saved) return false;
    applyVisitorFilterSnapshot(saved);
    return true;
  }

  function resetVisitorFilters() {
    search.value = "";
    liveOnly.value = false;
    consentFilter.value = "all";
    confidenceFilter.value = "all";
    intentFilter.value = "all";
    acquisitionFilter.value = "all";
    confidenceMin.value = 0;
    countryFilter.value = "all";
    countryFilters.value = [];
    mapEntityKeys.value = [];
    activityClassFilters.value = ["visitor", "recognized"];
    deviceFilters.value = [];
    datePreset.value = "today";
    const range = dateRangeForPreset("today");
    dateFrom.value = range.from;
    dateTo.value = range.to;
    frequencyFilter.value = "all";
    revealFilter.value = "all";
    targetFilter.value = "all";
    crmFilter.value = "all";
    sort.value = "recent";
  }

  function saveVisitorFilter(name: string) {
    const normalized = name.trim().slice(0, 80);
    if (!normalized) return null;
    const existing = savedVisitorFilters.value.find((view) => view.name.toLocaleLowerCase() === normalized.toLocaleLowerCase());
    const view: SavedVisitorFilter = {
      id: existing?.id ?? crypto.randomUUID(),
      name: normalized,
      filters: currentVisitorFilterSnapshot()
    };
    savedVisitorFilters.value = [view, ...savedVisitorFilters.value.filter((candidate) => candidate.id !== view.id)].slice(0, 20);
    writeSavedFilters(siteId.value, savedVisitorFilters.value);
    return view;
  }

  function deleteVisitorFilter(id: string) {
    savedVisitorFilters.value = savedVisitorFilters.value.filter((view) => view.id !== id);
    writeSavedFilters(siteId.value, savedVisitorFilters.value);
  }

  /** Moves one visit or all visits of an activity to the company they really came from (and optionally remembers it). */
  async function correctCompany(entityId: string, request: CompanyCorrectionRequest): Promise<CompanyCorrectionResult> {
    if (!firebaseConfigured) {
      // Demo data lives only in this browser: relabel the loaded entry.
      const entity = entities.value.find((item) => item.id === entityId);
      const existing = "organizationId" in request.target
        ? entities.value.find((item) => item.organizationId === (request.target as { organizationId: string }).organizationId)
        : undefined;
      const name = "name" in request.target ? request.target.name.trim() : existing?.companyName ?? "";
      const domain = "name" in request.target ? request.target.domain?.trim() || undefined : existing?.domain;
      const organizationId = "organizationId" in request.target ? request.target.organizationId : `org-demo-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      if (entity) {
        Object.assign(entity, { companyName: name, organizationId, domain, website: domain ? `https://${domain}` : undefined,
          logoUrl: undefined, confidence: 1, confidenceLabel: "high" });
      }
      return { correctionId: "demo", organizationId, entityId, moved: { signals: 0, created: 0, sessions: 0, visits: request.scope === "all" ? entity?.sessions ?? 1 : 1 },
        learned: { requested: request.remember, addresses: 0, ranges: 0, alias: false, visitor: false } };
    }
    const result = await apiFetch<CompanyCorrectionResult>(`/admin/sites/${encodeURIComponent(siteId.value)}/company-corrections`, {
      method: "POST",
      body: JSON.stringify(request)
    });
    await refreshWorkspace({ silent: true, kind: "admin" });
    if (!entities.value.some((item) => item.id === result.entityId)) {
      const account = await apiFetch<WorkspaceEntity>(`/admin/sites/${encodeURIComponent(siteId.value)}/accounts/${encodeURIComponent(result.organizationId)}`).catch(() => undefined);
      if (account) entities.value = [account, ...entities.value.filter((item) => item.id !== account.id)];
    }
    if (entities.value.some((item) => item.id === result.entityId)) selectEntity(result.entityId, result.organizationId);
    return result;
  }

  async function setTargetAccount(id: string, active: boolean) {
    const entity = entities.value.find((item) => item.id === id);
    if (!entity) return;
    const previous = entity.targetAccount;
    entity.targetAccount = active;
    if (!firebaseConfigured) {
      targetAccountOverrides.value[id] = active;
      localStorage.setItem(TARGET_ACCOUNTS_KEY, JSON.stringify(targetAccountOverrides.value));
      return;
    }
    try {
      if (!entity.organizationId) throw new Error("Für diesen Eintrag ist keine Organisation verknüpft.");
      await apiFetch(`/admin/sites/${encodeURIComponent(siteId.value)}/organizations/${encodeURIComponent(entity.organizationId)}/target-account`, {
        method: "PATCH",
        body: JSON.stringify({ active })
      });
    } catch (error) {
      entity.targetAccount = previous;
      throw error;
    }
  }

  function mapRequestTrace(trace: Record<string, any>): RequestTraceView {
    return {
      requestTraceId: String(trace.requestTraceId ?? trace.id), siteId: String(trace.siteId), occurredAt: String(trace.occurredAt), mode: trace.mode, consentStatus: trace.consentStatus, status: trace.status, routeCategory: String(trace.routeCategory), acquisitionCategory: trace.acquisitionCategory, networkClass: String(trace.networkClass), asn: trace.asn, asnName: trace.asnName, networkCountryCode: trace.networkCountryCode, networkLocation: trace.networkLocation,
      accountActivityWindowId: trace.accountActivityWindowId,
      accountActivityWindowStartedAt: trace.accountActivityWindowStartedAt,
      accountActivityWindowMinutes: trace.accountActivityWindowMinutes,
      linkedVisitorProfileId: trace.linkedVisitorProfileId,
      linkedSessionId: trace.linkedSessionId,
      routeLabel: trace.routeLabel,
      acquisitionSource: trace.acquisitionSource,
      selectedOrganizationId: trace.selectedOrganizationId,
      selectedOrganization: trace.candidates?.find((candidate: any) => candidate.organizationId === trace.selectedOrganizationId)?.name,
      topScore: Number(trace.topScore ?? 0), pipelineStatus: trace.pipelineStatus, accountSignalDelivery: trace.accountSignalDelivery, resolverDurationMs: Number(trace.resolverDurationMs ?? 0), graphReleaseId: String(trace.graphReleaseId), modelReleaseId: String(trace.modelReleaseId), connectorVersion: String(trace.connectorVersion), suppressionReason: trace.suppressionReason, unknownReason: trace.unknownReason,
      candidates: (trace.candidates ?? []).map((candidate: any) => ({ ...candidate, evidence: (candidate.evidence ?? []).map((evidence: any) => ({ ...evidence, observedAt: evidence.observedAt ?? trace.occurredAt })) })), resolverSteps: trace.resolverSteps ?? []
    } as RequestTraceView;
  }

  function projectActivityHistory() {
    // Recent diagnostics may carry active consent links. They override the
    // minimised history row, which never persists visitor or session identity.
    const rows = [...new Map([...activityHistory.value, ...requestTraces.value]
      .map(trace => [trace.requestTraceId, trace])).values()];
    entities.value = canonicalWorkspaceEntities(synchronizeAccountTraceActivities(entities.value, rows));
    ensureVisibleSelection({ hydrate: false });
  }

  async function refreshActivityHistory(append = false, preserveLoaded = false) {
    if (!firebaseConfigured || organizationSwitching.value || !siteId.value || !activityHistoryAvailable) return;
    const epoch = contextEpoch, request = ++activityHistoryRequest;
    const query = new URLSearchParams({ view: "activity", limit: "250" });
    if (dateFrom.value) query.set("from", new Date(`${dateFrom.value}T00:00:00`).toISOString());
    if (dateTo.value) query.set("to", new Date(`${dateTo.value}T23:59:59.999`).toISOString());
    if (append && activityHistoryCursor.value) query.set("cursor", activityHistoryCursor.value);
    try {
      const page = await apiFetch<{ items: Array<Record<string, any>>; nextCursor: string | null; hasMore: boolean }>(
        `/admin/sites/${encodeURIComponent(siteId.value)}/request-traces?${query}`);
      if (epoch !== contextEpoch || request !== activityHistoryRequest) return;
      const mapped = page.items.map(row => ({ ...mapRequestTrace(row), activityHistoryExpiresAt:
        typeof row.expiresAt === 'string' ? Date.parse(row.expiresAt) : Number(row.expiresAt?._seconds ?? row.expiresAt?.seconds) * 1000 }));
      const keepCursor = preserveLoaded && activityHistory.value.length > 0;
      activityHistory.value = (append || preserveLoaded ? [...new Map([...activityHistory.value, ...mapped].map(row => [row.requestTraceId, row])).values()] : mapped)
        .filter(row => !Number.isFinite(row.activityHistoryExpiresAt) || row.activityHistoryExpiresAt! > Date.now());
      if (!keepCursor) {
        activityHistoryCursor.value = page.nextCursor;
        activityHistoryHasMore.value = page.hasMore;
      }
      projectActivityHistory();
    } catch (error) {
      if (epoch === contextEpoch && request === activityHistoryRequest)
        workspaceError.value = error instanceof Error ? error.message : "Aktivitätshistorie konnte nicht geladen werden.";
    }
  }
  watch([dateFrom, dateTo], () => { void refreshActivityHistory(); });

  async function refreshRequestTraces(options: { append?: boolean; filters?: TraceQueryFilters } = {}) {
    if (!firebaseConfigured || organizationSwitching.value || !siteId.value) return;
    const epoch = contextEpoch;
    if (options.filters) traceQueryFilters.value = { ...options.filters };
    traceLoading.value = true; traceError.value = "";
    try {
      const query = new URLSearchParams({ limit: "250" });
      if (traceStatus.value !== "all") query.set("status", traceStatus.value);
      Object.entries(traceQueryFilters.value).forEach(([key, value]) => { if (value) query.set(key, value); });
      if (options.append && traceNextCursor.value) query.set("cursor", traceNextCursor.value);
      const response = await apiFetch<{
        items: Array<Record<string, any>>;
        nextCursor: string | null;
        hasMore: boolean;
        scanLimitReached: boolean;
      }>(`/admin/sites/${encodeURIComponent(siteId.value)}/request-traces?${query}`);
      if (epoch !== contextEpoch) return;
      const mapped = response.items.map(mapRequestTrace);
      requestTraces.value = options.append
        ? [...new Map([...requestTraces.value, ...mapped].map((trace) => [trace.requestTraceId, trace])).values()]
        : mapped;
      const isUnfilteredTracePage = traceStatus.value === "all"
        && !Object.values(traceQueryFilters.value).some(Boolean);
      if (isUnfilteredTracePage) {
        // Trace-only candidates are projected after account/visitor summaries
        // arrive. Canonicalize once more so a unique short consent transition
        // cannot reappear as a duplicate Account-Mode row beside its visitor.
        projectActivityHistory();
        // A low-score company may exist only in the trace projection. Re-run
        // selection after those rows arrive, but never rewrite the user's
        // explicit Identified filter to All while the trace page is loading.
        ensureVisibleSelection({ hydrate: false });
      }
      traceNextCursor.value = response.nextCursor;
      traceHasMore.value = response.hasMore;
      traceScanLimitReached.value = response.scanLimitReached;
    } catch (error) { if (epoch === contextEpoch) traceError.value = error instanceof Error ? error.message : "Request traces could not be loaded."; }
    finally { if (epoch === contextEpoch) traceLoading.value = false; }
  }

  async function loadMoreRequestTraces() {
    if (!traceHasMore.value || !traceNextCursor.value || traceLoading.value) return;
    await refreshRequestTraces({ append: true });
  }

  async function refreshStatistics(days: 7 | 30 | 90 = statisticsDays.value) {
    if (firebaseConfigured && (organizationSwitching.value || !siteId.value)) return;
    const epoch = contextEpoch;
    statisticsDays.value = days;
    if (!firebaseConfigured) {
      const series = demoTimeSeries.slice(-days);
      const totals = series.reduce((sum, item) => ({
        totalRequests: sum.totalRequests + item.totalRequests,
        identified: sum.identified + item.identified,
        candidates: sum.candidates + item.candidates,
        unknown: sum.unknown + item.unknown
      }), { totalRequests: 0, identified: 0, candidates: 0, unknown: 0 });
      const periodScale = totals.totalRequests / demoStatistics.totalRequests;
      const scaleCount = (value: number) => Math.max(value ? 1 : 0, Math.round(value * periodScale));
      statistics.value = {
        ...demoStatistics,
        ...totals,
        suppressed: scaleCount(demoStatistics.suppressed),
        resolverErrors: scaleCount(demoStatistics.resolverErrors),
        consentedVisitors: scaleCount(demoStatistics.consentedVisitors),
        reveals: scaleCount(demoStatistics.reveals),
        newCompanies: scaleCount(demoStatistics.newCompanies),
        returningCompanies: scaleCount(demoStatistics.returningCompanies),
        newVisitors: scaleCount(demoStatistics.newVisitors),
        returningVisitors: scaleCount(demoStatistics.returningVisitors),
        conversions: scaleCount(demoStatistics.conversions),
        topCategories: demoStatistics.topCategories.map((item) => ({ ...item, count: scaleCount(item.count) })),
        topCompanies: demoStatistics.topCompanies.map((item) => ({ ...item, count: scaleCount(item.count) })),
        topRegions: demoStatistics.topRegions.map((item) => ({ ...item, count: scaleCount(item.count) })),
        deviceBreakdown: demoStatistics.deviceBreakdown.map((item) => ({ ...item, count: scaleCount(item.count) })),
        acquisitionBreakdown: demoStatistics.acquisitionBreakdown.map((item) => ({ ...item, count: scaleCount(item.count) })),
        journeyFlows: demoStatistics.journeyFlows.map((item) => ({ ...item, count: scaleCount(item.count) })),
        consentBreakdown: demoStatistics.consentBreakdown.map((item) => ({ ...item, count: scaleCount(item.count) })),
        pageAnalytics: demoStatistics.pageAnalytics.map((page) => ({
          ...page,
          views: scaleCount(page.views),
          uniqueVisitors: scaleCount(page.uniqueVisitors),
          totalEngagementMs: Math.round(page.totalEngagementMs * periodScale),
          conversionAssists: scaleCount(page.conversionAssists),
          companyTypes: page.companyTypes.map((item) => ({ ...item, count: scaleCount(item.count) }))
        })),
        period: {
          days,
          from: series[0]?.date ? new Date(`${series[0].date}T00:00:00.000Z`).toISOString() : demoStatistics.period.from,
          to: demoStatistics.period.to,
          dataSource: "demo"
        },
        timeSeries: series
      };
      return;
    }
    const response = await apiFetch<DashboardStatistics>(
      `/admin/sites/${encodeURIComponent(siteId.value)}/statistics?days=${days}`
    );
    if (epoch === contextEpoch && statisticsDays.value === days) statistics.value = response;
  }

  async function refreshWorkspace(options: { silent?: boolean; kind?: LiveRevisionKind } = {}) {
    if (!firebaseConfigured || organizationSwitching.value) return;
    const epoch = contextEpoch;
    if (refreshingEpoch === epoch) {
      if (options.silent) {
        pendingSilentRefresh = true;
        pendingLiveKind = mergeLiveKinds(pendingLiveKind, options.kind);
      }
      return;
    }
    refreshingEpoch = epoch;
    if (!options.silent) workspaceLoading.value = true;
    workspaceError.value = "";
    try {
      if (!availableSites.value.length) {
        const sites = await apiFetch<{ items: Array<{ id: string; name?: string }> }>("/admin/sites");
        if (epoch !== contextEpoch) return;
        availableSites.value = sites.items.map((site) => ({ ...site, name: site.name || site.id }));
      }
      const selectedSite = availableSites.value.find((site) => site.id === siteId.value) ?? availableSites.value[0];
      if (!selectedSite) {
        siteId.value = "";
        siteName.value = "Keine Website";
        entities.value = [];
        requestTraces.value = [];
        workspaceError.value = "";
        lastRefreshedAt.value = new Date().toISOString();
        return;
      }
      siteId.value = selectedSite.id;
      siteName.value = selectedSite.name;
      const encodedSite = encodeURIComponent(siteId.value);
      const kind = options.kind;
      const refreshAccounts = !options.silent || !kind || kind === "account" || kind === "admin";
      const refreshVisitors = !options.silent || !kind || ["consent", "session", "heartbeat", "event", "reveal", "replay", "admin"].includes(kind);
      const refreshDashboard = !options.silent || !kind || !["heartbeat"].includes(kind);
      const refreshConnector = !options.silent || !kind || ["account", "trace", "admin"].includes(kind);
      const refreshTraces = !options.silent || !kind || ["trace", "admin"].includes(kind);
      const [bootstrap, accounts, visitors, dashboard, connector, scoringSettings] = await Promise.all([
        !options.silent || !kind ? apiFetch<{ id: string; name?: string; activityHistoryAvailable?: boolean }>(`/admin/sites/${encodedSite}/bootstrap`) : Promise.resolve(null),
        refreshAccounts ? apiFetch<EntityPageResponse>(`/admin/sites/${encodedSite}/accounts?limit=250`) : Promise.resolve(null),
        refreshVisitors ? apiFetch<EntityPageResponse>(`/admin/sites/${encodedSite}/visitors?limit=250`) : Promise.resolve(null),
        refreshDashboard ? apiFetch<DashboardStatistics>(`/admin/sites/${encodedSite}/statistics?days=${statisticsDays.value}`) : Promise.resolve(null),
        refreshConnector ? apiFetch<ConnectorHealth>(`/admin/sites/${encodedSite}/connector-health`) : Promise.resolve(null),
        !options.silent || !kind
          ? apiFetch<SettingsFieldsResponse>(`/admin/sites/${encodedSite}/settings/categories`).catch(() => null)
          : Promise.resolve(null)
      ]);
      if (epoch !== contextEpoch) return;
      if (bootstrap) {
        siteName.value = bootstrap.name || bootstrap.id || siteId.value;
        activityHistoryAvailable = bootstrap.activityHistoryAvailable === true;
      }
      if (scoringSettings) {
        leadFitWeights.value = leadFitWeightsFromFields(scoringSettings.fields);
        setActiveLeadFitWeights(leadFitWeights.value);
      }
      if (accounts || visitors) {
        const selectedBeforeRefresh = entities.value.find((entity) => entity.id === selectedId.value);
        const summaries = [...(accounts?.items ?? []), ...(visitors?.items ?? [])];
        const summaryIds = new Set(summaries.map((summary) => summary.id));
        const retained = entities.value.filter((entity) => (
          // Keep the current trace projection until the trace response is
          // ready. Dropping it here and rebuilding it a moment later was the
          // remaining full-refresh flash in the activity list.
          entity.traceActivity
          || (entity.kind === "account" && !accounts)
          || (entity.kind === "visitor" && !visitors)
          || (options.silent && entity.id === selectedId.value && !summaryIds.has(entity.id))
        ));
        const existing = new Map(entities.value.map((entity) => [entity.id, entity]));
        summaries.forEach((summary) => {
          const current = existing.get(summary.id);
          existing.set(summary.id, current ? mergeWorkspaceEntitySummary(current, summary) : summary);
        });
        entities.value = canonicalWorkspaceEntities([...retained, ...summaries.map((summary) => existing.get(summary.id)!)]);
        if (selectedBeforeRefresh && !entities.value.some((entity) => entity.id === selectedId.value)) {
          const canonicalSelection = entities.value.find((entity) =>
            Boolean(selectedBeforeRefresh.visitorProfileId)
              && entity.visitorProfileId === selectedBeforeRefresh.visitorProfileId
          ) ?? entities.value.find((entity) =>
            mapActivityIdentityKey(entity) === mapActivityIdentityKey(selectedBeforeRefresh)
          );
          if (canonicalSelection) selectedId.value = canonicalSelection.id;
        }
      }
      if (accounts && (!options.silent || entities.value.filter((entity) => entity.kind === "account").length <= accounts.items.length)) {
        accountNextCursor.value = accounts.nextCursor;
        accountHasMore.value = accounts.hasMore;
      }
      if (visitors && (!options.silent || entities.value.filter((entity) => entity.kind === "visitor").length <= visitors.items.length)) {
        visitorNextCursor.value = visitors.nextCursor;
        visitorHasMore.value = visitors.hasMore;
      }
      if (dashboard) statistics.value = dashboard;
      if (connector) connectorHealth.value = connector;
      const nextId = ensureVisibleSelection({ hydrate: false });
      const detailKinds: LiveRevisionKind[] = ["account", "consent", "session", "event", "reveal", "replay", "admin"];
      if (nextId) await hydrateEntity(nextId, { force: Boolean(options.silent && (!kind || detailKinds.includes(kind))) });
      if (epoch !== contextEpoch) return;
      if (refreshTraces) await refreshRequestTraces();
      if (epoch !== contextEpoch) return;
      if (refreshTraces) await refreshActivityHistory(false, Boolean(options.silent));
      if (epoch !== contextEpoch) return;
      if (!refreshTraces) projectActivityHistory();
      updateLiveFlags();
      lastRefreshedAt.value = new Date().toISOString();
    } catch (error) {
      if (epoch === contextEpoch) workspaceError.value = error instanceof Error ? error.message : "Workspace-Daten konnten nicht geladen werden.";
    } finally {
      if (epoch === contextEpoch && !options.silent) workspaceLoading.value = false;
      if (refreshingEpoch === epoch) refreshingEpoch = undefined;
      if (epoch === contextEpoch && pendingSilentRefresh) {
        pendingSilentRefresh = false;
        const nextKind = pendingLiveKind;
        pendingLiveKind = undefined;
        queueMicrotask(() => { if (epoch === contextEpoch) void refreshWorkspace({ silent: true, kind: nextKind }); });
      }
    }
  }

  async function refreshSiteDirectory() {
    if (!firebaseConfigured) return availableSites.value;
    const epoch = contextEpoch;
    const sites = await apiFetch<{ items: Array<Omit<SiteDirectoryItem, "name"> & { name?: string }> }>("/admin/sites");
    if (epoch !== contextEpoch) return availableSites.value;
    availableSites.value = sites.items.map((site) => ({ ...site, name: site.name || site.id }));
    return availableSites.value;
  }

  async function refreshOrganizations() {
    if (!firebaseConfigured) return organizations.value;
    const epoch = contextEpoch;
    const status = await apiFetch<{ organizations?: OrganizationDirectoryItem[] }>("/onboarding/status");
    if (epoch !== contextEpoch) return organizations.value;
    organizations.value = status.organizations ?? [];
    return organizations.value;
  }

  async function switchOrganization(tenantId: string) {
    // Serialize token-changing operations, including rapid A -> B clicks.
    const operation = organizationSwitchTail.then(async () => {
      if (!firebaseConfigured || organizations.value.find((organization) => organization.id === tenantId)?.active) return;
      organizationSwitching.value = true;
      resetWorkspaceContext();
      availableSites.value = [];
      siteId.value = "";
      siteName.value = "Site wird geladen";
      workspaceLoading.value = true;
      try {
        await apiFetch("/onboarding/organizations/activate", { method: "POST", body: JSON.stringify({ tenantId }) });
        await firebaseAuth?.currentUser?.getIdToken(true);
      } finally {
        // Activation can commit its directory update before an Auth error.
        // Recover the authoritative selection instead of retaining the old menu.
        await refreshOrganizations().catch(() => undefined);
        organizationSwitching.value = false;
        await refreshWorkspace();
        await startLiveUpdates();
      }
    });
    organizationSwitchTail = operation.catch(() => undefined);
    return operation;
  }

  async function deleteSite(targetSiteId: string, reason: string) {
    if (!firebaseConfigured) return;
    await apiFetch(`/admin/sites/${encodeURIComponent(targetSiteId)}`, {
      method: "DELETE",
      body: JSON.stringify({ confirmSiteId: targetSiteId, reason })
    });
    resetWorkspaceContext();
    await refreshSiteDirectory();
    const next = availableSites.value.find((site) => site.id !== targetSiteId) ?? availableSites.value[0];
    siteId.value = next?.id ?? "";
    siteName.value = next?.name ?? "Keine Website";
    selectedId.value = "";
    selectedOrganizationContextId.value = "";
    entities.value = [];
    requestTraces.value = [];
    if (next) {
      await refreshWorkspace();
      await startLiveUpdates();
    }
  }

  function updateLiveFlags() {
    const now = Date.now();
    let changed = false;
    const next = entities.value.map((entity) => {
      const fallbackMs = entity.kind === "account" ? 2 * 60_000 : 35_000;
      const leaseExpiry = entity.liveUntil ? new Date(entity.liveUntil).getTime() : Number.NaN;
      const live = Number.isFinite(leaseExpiry)
        ? leaseExpiry > now && leaseExpiry - now <= Math.max(3 * 60_000, fallbackMs + 5_000)
        : now - new Date(entity.lastSeenAt).getTime() <= fallbackMs;
      if (live === entity.live) return entity;
      changed = true;
      return { ...entity, live };
    });
    // Replacing every entity every 15 seconds caused the virtual activity list
    // to visibly repaint even when no live state had changed.
    if (changed) entities.value = next;
  }

  function applyLiveRevision(revision: string, kind?: LiveRevisionKind) {
    liveSyncStatus.value = "live";
    liveSyncError.value = "";
    if (!revision) return;
    if (!liveRevision) {
      liveRevision = revision;
      return;
    }
    if (revision !== liveRevision) {
      liveRevision = revision;
      void refreshWorkspace({ silent: true, kind });
    }
  }

  async function pollLiveRevision() {
    if (!firebaseConfigured || !firebaseAuth?.currentUser || !siteId.value || document.visibilityState === "hidden") return;
    const epoch = contextEpoch;
    try {
      const response = await apiFetch<{ revision: string; kind?: LiveRevisionKind }>(`/admin/sites/${encodeURIComponent(siteId.value)}/live-revision`);
      if (epoch === contextEpoch) applyLiveRevision(response.revision, response.kind);
    } catch (error) {
      if (epoch === contextEpoch && !liveUnsubscribe) {
        liveSyncStatus.value = "error";
        liveSyncError.value = error instanceof Error ? error.message : "Live-Status konnte nicht geprüft werden.";
      }
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState !== "visible") return;
    // The revision endpoint and Firestore listener are the source of truth.
    // A second unconditional full refresh on both focus and visibility caused
    // duplicate requests and short-lived list reordering.
    void pollLiveRevision();
  }

  function stopLiveUpdates() {
    liveUnsubscribe?.();
    liveUnsubscribe = undefined;
    if (livePollTimer !== undefined) window.clearInterval(livePollTimer);
    if (liveAgeTimer !== undefined) window.clearInterval(liveAgeTimer);
    livePollTimer = undefined;
    liveAgeTimer = undefined;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    liveRevision = "";
    if (firebaseConfigured) liveSyncStatus.value = "off";
  }

  async function startLiveUpdates() {
    stopLiveUpdates();
    if (!firebaseConfigured || !firebaseAuth?.currentUser || !firebaseDb || !siteId.value) return;
    const epoch = contextEpoch;
    liveSyncStatus.value = "connecting";
    liveSyncError.value = "";
    try {
      let token = await firebaseAuth.currentUser.getIdTokenResult();
      if (epoch !== contextEpoch) return;
      let tenantId = typeof token.claims.tenantId === "string" ? token.claims.tenantId : "";
      // Accounts accepted through the directory-backed invitation flow can
      // still hold the pre-acceptance token. Refresh once before declaring the
      // live channel broken so freshly synchronized claims take effect now.
      if (!tenantId) {
        token = await firebaseAuth.currentUser.getIdTokenResult(true);
        if (epoch !== contextEpoch) return;
        tenantId = typeof token.claims.tenantId === "string" ? token.claims.tenantId : "";
      }
      if (!tenantId) {
        await apiFetch("/onboarding/sync-access-claims", { method: "POST" });
        if (epoch !== contextEpoch) return;
        token = await firebaseAuth.currentUser.getIdTokenResult(true);
        tenantId = typeof token.claims.tenantId === "string" ? token.claims.tenantId : "";
      }
      if (!tenantId) throw new Error("Tenant-Claim fehlt; Live-Updates können nicht abonniert werden.");
      if (epoch !== contextEpoch) return;
      liveUnsubscribe = onSnapshot(
        doc(firebaseDb, "tenants", tenantId, "sites", siteId.value, "liveState", "current"),
        (snapshot) => {
          const revision = typeof snapshot.data()?.revision === "string" ? snapshot.data()!.revision as string : "";
          const kind = typeof snapshot.data()?.kind === "string" ? snapshot.data()!.kind as LiveRevisionKind : undefined;
          if (epoch === contextEpoch) applyLiveRevision(revision, kind);
        },
        (error) => {
          if (epoch !== contextEpoch) return;
          liveSyncStatus.value = "error";
          liveSyncError.value = error.message;
          void pollLiveRevision();
        }
      );
      livePollTimer = window.setInterval(() => void pollLiveRevision(), 5_000);
      liveAgeTimer = window.setInterval(updateLiveFlags, 3_000);
      document.addEventListener("visibilitychange", onVisibilityChange);
      updateLiveFlags();
      void pollLiveRevision();
    } catch (error) {
      if (epoch !== contextEpoch) return;
      liveSyncStatus.value = "error";
      liveSyncError.value = error instanceof Error ? error.message : "Live-Updates konnten nicht gestartet werden.";
    }
  }

  async function switchSite(nextSiteId: string) {
    if (nextSiteId === siteId.value) return;
    const next = availableSites.value.find((site) => site.id === nextSiteId);
    if (!next) throw new Error("Die gewählte Site gehört nicht zu diesem Mandanten.");
    resetWorkspaceContext();
    siteId.value = next.id;
    siteName.value = next.name;
    selectedId.value = "";
    selectedOrganizationContextId.value = "";
    mapEntityKeys.value = [];
    entities.value = [];
    requestTraces.value = [];
    traceNextCursor.value = null;
    traceHasMore.value = false;
    traceScanLimitReached.value = false;
    traceQueryFilters.value = {};
    accountNextCursor.value = null;
    accountHasMore.value = false;
    visitorNextCursor.value = null;
    visitorHasMore.value = false;
    savedVisitorFilters.value = readSavedFilters(next.id);
    leadFitWeights.value = { ...defaultLeadFitWeights };
    setActiveLeadFitWeights(leadFitWeights.value);
    stopLiveUpdates();
    await refreshWorkspace();
    await startLiveUpdates();
  }

  return {
    siteId,
    canViewQuality,
    siteName,
    availableSites,
    organizations,
    organizationSwitching,
    activeOrganizationName,
    entities,
    requestTraces,
    activityHistoryHasMore,
    statistics,
    connectorHealth,
    statisticsDays,
    mode,
    activeDetailTab,
    selectedId,
    selectedTraceId,
    search,
    liveOnly,
    consentFilter,
    confidenceFilter,
    intentFilter,
    acquisitionFilter,
    confidenceMin,
    countryFilter,
    countryFilters,
    mapEntityKeys,
    activityClassFilters,
    deviceFilters,
    datePreset,
    dateFrom,
    dateTo,
    frequencyFilter,
    revealFilter,
    targetFilter,
    crmFilter,
    sort,
    activitySummary,
    activitySummaryActive,
    activitySummaryForced,
    recognizedCompaniesOnly,
    botsVisible,
    savedVisitorFilters,
    traceStatus,
    listCollapsed,
    traceLoading,
    traceError,
    traceNextCursor,
    traceHasMore,
    traceScanLimitReached,
    traceQueryFilters,
    accountHasMore,
    visitorHasMore,
    entityPageLoading,
    mapCoverageLoading,
    workspaceLoading,
    workspaceError,
    lastRefreshedAt,
    liveSyncStatus,
    liveSyncError,
    filteredEntities,
    mapEntities,
    filteredActivityEntities,
    activityFacetEntities,
    selectedEntity,
    filteredTraces,
    selectedTrace,
    setMode,
    setDatePreset,
    toggleActivityClass,
    toggleDevice,
    toggleCountry,
    ensureVisibleSelection,
    selectEntity,
    hydrateEntity,
    enrichOrganization,
    loadMoreEntities,
    toggleList,
    toggleActivitySummary,
    currentVisitorFilterSnapshot,
    applyVisitorFilterSnapshot,
    persistVisitorFilters,
    restoreVisitorFilters,
    resetVisitorFilters,
    leadFitWeights,
    saveVisitorFilter,
    deleteVisitorFilter,
    setTargetAccount,
    correctCompany,
    refreshRequestTraces,
    loadMoreRequestTraces,
    refreshStatistics,
    refreshWorkspace,
    refreshSiteDirectory,
    refreshOrganizations,
    startLiveUpdates,
    stopLiveUpdates,
    switchSite,
    switchOrganization,
    deleteSite
  };
});
